'use client'

import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PublicationDraft, PublicationTextPart } from '@/lib/publications/types'
import { publicationThemes } from '@/lib/publications/templates'

interface Props {
  draft: PublicationDraft
}

function renderTextParts(
  parts: PublicationTextPart[] | undefined,
  fallback: string,
  design: NonNullable<PublicationDraft['design']>
) {
  if (!parts?.length) return fallback

  return parts.map((part, index) => (
    <span
      key={`${part.text}-${index}`}
      style={{
        color: part.accent ? design.accent : part.muted ? design.textMuted : design.text,
        fontStyle: part.italic ? 'italic' : 'normal',
        fontFamily: part.italic
          ? design.serifFont || design.displayFont
          : design.displayFont,
        fontWeight: part.accent ? 500 : 400,
      }}
    >
      {part.text}
    </span>
  ))
}

function BotanicalDecor({
  design,
}: {
  design: NonNullable<PublicationDraft['design']>
}) {
  return (
    <>
      <div
        className="absolute -right-8 top-10 h-40 w-40 rounded-full blur-3xl"
        style={{ background: design.accentSoft, opacity: 0.45 }}
      />
      <div
        className="absolute -left-10 bottom-8 h-44 w-44 rounded-full blur-3xl"
        style={{ background: design.backgroundSoft || design.accentSoft, opacity: 0.7 }}
      />

      <div className="absolute right-8 top-24 rotate-12 opacity-50">
        <div className="relative h-28 w-20">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="absolute h-10 w-5 rounded-full"
              style={{
                left: `${i * 12}px`,
                top: `${i * 16}px`,
                transform: `rotate(${i % 2 ? -38 : 38}deg)`,
                background: `linear-gradient(135deg, ${design.accentSoft}, ${design.accent})`,
                opacity: 0.42,
              }}
            />
          ))}
        </div>
      </div>

      <div className="absolute bottom-16 right-16 opacity-40">
        <div
          className="h-20 w-20 rounded-full"
          style={{
            border: `1px solid ${design.accent}`,
            background: `radial-gradient(circle at 30% 30%, ${design.surface}, transparent 62%)`,
          }}
        />
      </div>

      <div className="absolute left-8 top-24 opacity-35">
        <div
          className="h-24 w-24 rounded-full"
          style={{
            background: `radial-gradient(circle, ${design.surface} 0%, transparent 68%)`,
          }}
        />
      </div>
    </>
  )
}

export default function TemplateCarouselPremium({ draft }: Props) {
  const theme = publicationThemes[draft.themeId]
  const slides = draft.slides || []
  const [activeIndex, setActiveIndex] = useState(0)

  const slide = useMemo(() => {
    return slides[Math.min(activeIndex, Math.max(0, slides.length - 1))]
  }, [slides, activeIndex])

  const design =
    draft.design || {
      background: '#F6EEE4',
      backgroundSoft: '#EBDAC5',
      surface: 'rgba(255,255,255,0.72)',
      surfaceStrong: '#FFFFFF',
      text: '#33261D',
      textMuted: '#6A5545',
      accent: '#7C8B5A',
      accentSoft: '#DDE5D3',
      accentDeep: '#40543B',
      border: 'rgba(80,60,40,0.12)',
      shadow: '0 24px 70px rgba(74, 54, 32, 0.16)',
      displayFont: 'Playfair Display, Georgia, serif',
      serifFont: 'Playfair Display, Georgia, serif',
      bodyFont: 'Montserrat, Inter, system-ui, sans-serif',
      logoTracking: '0.28em',
      cornerRadius: 34,
      decorativeStyle: 'botanical' as const,
    }

  if (!slides.length || !slide) return null

  const prev = () => setActiveIndex((p) => (p - 1 + slides.length) % slides.length)
  const next = () => setActiveIndex((p) => (p + 1) % slides.length)

  const isFirst = activeIndex === 0
  const isLast = activeIndex === slides.length - 1

  return (
    <div className="space-y-3">
      <div
        className="relative aspect-square overflow-hidden"
        style={{
          borderRadius: design.cornerRadius || 34,
          background: `linear-gradient(135deg, ${design.background}, ${design.backgroundSoft || design.background})`,
          boxShadow: design.shadow,
        }}
      >
        <BotanicalDecor design={design} />

        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(80,60,40,0.35) 1px, transparent 0)',
            backgroundSize: '18px 18px',
          }}
        />

        <div className="relative z-10 flex h-full flex-col p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div
                className="text-[10px] uppercase"
                style={{
                  color: design.textMuted,
                  letterSpacing: design.logoTracking || '0.24em',
                  fontFamily: design.bodyFont,
                }}
              >
                {draft.brand?.name || 'RIMAN'}
              </div>
              <div
                className="mt-1 text-[8px] uppercase tracking-[0.18em]"
                style={{
                  color: design.textMuted,
                  fontFamily: design.bodyFont,
                  opacity: 0.78,
                }}
              >
                Korean Skincare
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full transition hover:scale-105"
                style={{
                  background: design.surface,
                  color: design.text,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={next}
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full transition hover:scale-105"
                style={{
                  background: design.surface,
                  color: design.text,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid flex-1 grid-cols-[1.05fr_0.95fr] items-center gap-4">
            <div className={isFirst ? 'self-center' : 'self-center'}>
              {slide.eyebrow ? (
                <div
                  className="mb-4 inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase"
                  style={{
                    color: design.accentDeep || design.accent,
                    background: design.surface,
                    letterSpacing: '0.18em',
                    fontFamily: design.bodyFont,
                  }}
                >
                  {slide.eyebrow}
                </div>
              ) : null}

              <h3
                className="whitespace-pre-line text-[40px] leading-[0.98]"
                style={{
                  color: design.text,
                  fontFamily: design.displayFont,
                  fontWeight: 400,
                }}
              >
                {renderTextParts(slide.headlineParts, slide.headline, design)}
              </h3>

              {slide.subheadline ? (
                <p
                  className="mt-5 max-w-[95%] text-[15px] leading-6"
                  style={{
                    color: design.textMuted,
                    fontFamily: design.bodyFont,
                  }}
                >
                  {slide.subheadline}
                </p>
              ) : null}

              {slide.body ? (
                <p
                  className="mt-3 max-w-[92%] text-[12px] leading-5"
                  style={{
                    color: design.textMuted,
                    fontFamily: design.bodyFont,
                    opacity: 0.84,
                  }}
                >
                  {slide.body}
                </p>
              ) : null}
            </div>

            <div className="relative flex h-full items-center justify-center">
              {slide.imageUrl || slide.productImageUrl ? (
                <img
                    src={slide.productImageUrl || slide.imageUrl || ''}
                    alt={slide.headline}
                    className="max-h-[82%] max-w-[92%] object-contain drop-shadow-[0_28px_55px_rgba(0,0,0,0.18)]"
                />
                ) : (
                <div className="relative h-[74%] w-[86%]">
                    <div
                    className="absolute inset-0 rounded-[38px]"
                    style={{
                        background: `linear-gradient(145deg, ${design.surface}, transparent 72%)`,
                        border: `1px solid ${design.border}`,
                        opacity: 0.42,
                    }}
                    />

                    <div
                    className="absolute left-1/2 top-1/2 h-[72%] w-[58%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-2xl"
                    style={{
                        background: design.accentSoft,
                        opacity: 0.42,
                    }}
                    />

                    <div
                    className="absolute bottom-6 right-4 h-20 w-20 rounded-full"
                    style={{
                        border: `1px solid ${design.accent}`,
                        opacity: 0.24,
                    }}
                    />

                    <div
                    className="absolute right-8 top-8 h-28 w-16 rounded-full"
                    style={{
                        background: `linear-gradient(135deg, ${design.accentSoft}, transparent)`,
                        opacity: 0.28,
                        transform: 'rotate(18deg)',
                    }}
                    />
                </div>
                )}
            </div>
          </div>

          <div className="flex items-end justify-between">
            <div className="flex items-center gap-1.5">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: idx === activeIndex ? 22 : 6,
                    background: idx === activeIndex ? design.text : design.textMuted,
                    opacity: idx === activeIndex ? 1 : 0.28,
                  }}
                />
              ))}
            </div>

            <div className="flex items-center gap-3">
              {slide.cta ? (
                <div
                  className="rounded-full px-4 py-2 text-[11px] font-semibold"
                  style={{
                    background: design.accent,
                    color: '#FFFFFF',
                    fontFamily: design.bodyFont,
                  }}
                >
                  {slide.cta}
                </div>
              ) : null}

              <div
                className="text-[10px]"
                style={{
                  color: design.textMuted,
                  fontFamily: design.bodyFont,
                }}
              >
                {activeIndex + 1}/{slides.length}
              </div>
            </div>
          </div>
        </div>

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20"
          style={{
            background: `linear-gradient(to top, ${design.background} 0%, transparent 100%)`,
            opacity: 0.55,
          }}
        />
      </div>
    </div>
  )
}